import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  MapPin, Globe, Mail, Phone, Store, HeartPulse, Facebook,
  Instagram, Edit, ShieldCheck, ArrowLeft, Handshake,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { EntityRelationsWidget } from "@/components/EntityRelationsWidget";

export default function DirectoryEntryDetailPage() {
  const { type, slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [entry, setEntry] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/directory/${slug}`),
      api.get("/directory/taxonomy"),
    ])
      .then(([resEntry, resTax]) => {
        setEntry(resEntry.data);
        setTaxonomy(resTax.data);
      })
      .catch(() => setEntry(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (loading) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8">
        <Skeleton className="h-8 w-1/2 mb-4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-8 text-center">
        <p className="text-muted-foreground">Fiche introuvable.</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Retour
        </Button>
      </div>
    );
  }

  const isSante = entry.type === "sante";
  const Icon = isSante ? HeartPulse : Store;
  const sectionLabel = isSante ? "Santé & Performance" : "Boutiques & Services";
  const backSlug = isSante ? "sante-performance" : "boutiques-services";

  const categoryLabel = taxonomy?.[entry.type]?.categories?.find((c) => c.slug === entry.category)?.label
    || entry.category?.replace(/_/g, " ");

  const isOwner = !!user && entry.owner_user_id === user.id;
  const isStaff = !!user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));
  const canEdit = isOwner || isStaff;

  const hero = entry.cover_url || entry.photo_url || entry.logo_url;

  const handleClaim = async () => {
    if (!user) {
      toast.info("Connectez-vous pour revendiquer cette fiche.");
      navigate("/login");
      return;
    }
    try {
      await api.post(`/directory/${entry.id}/claim`);
      toast.success("Fiche revendiquée avec succès.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la revendication");
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-4">
        <Link
          to={`/annuaire/${backSlug}`}
          className="text-xs text-muted-foreground hover:text-[hsl(var(--aito-lagoon))] inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3 w-3" /> Retour à {sectionLabel}
        </Link>
      </div>

      {hero && (
        <div className="aspect-[21/9] rounded-lg overflow-hidden bg-muted mb-6">
          <img src={hero} alt={entry.name} className="w-full h-full object-cover object-top" />
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-start gap-4">
                {entry.logo_url && (
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-muted shrink-0 border border-border/60">
                    <img src={entry.logo_url} alt="" className="w-full h-full object-contain p-1.5" />
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="h-4 w-4 text-[hsl(var(--aito-lagoon))]" />
                    <Badge variant="outline" className="text-[10px]">{categoryLabel}</Badge>
                  </div>
                  <h1 className="font-display font-bold text-2xl sm:text-3xl" data-testid="directory-entry-name">
                    {entry.name}
                  </h1>
                  {entry.short_motto && (
                    <p className="text-sm text-muted-foreground italic mt-1">{entry.short_motto}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {canEdit && (
                  <Link to={`/annuaire/${backSlug}/${entry.slug}/edit`}>
                    <Button variant="outline" size="sm" data-testid="directory-entry-edit-btn">
                      <Edit className="h-3.5 w-3.5 mr-1.5" /> Modifier
                    </Button>
                  </Link>
                )}
                {!entry.owner_user_id && user && (
                  <Button size="sm" onClick={handleClaim} data-testid="directory-entry-claim-btn">
                    <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> C'est ma fiche
                  </Button>
                )}
              </div>
            </div>

            {entry.description && (
              <p className="mt-4 text-sm leading-relaxed whitespace-pre-line" data-testid="directory-entry-description">
                {entry.description}
              </p>
            )}

            {(entry.disciplines || []).length > 0 && (
              <div className="mt-4">
                <div className="text-xs text-muted-foreground mb-1.5">Disciplines liées</div>
                <div className="flex flex-wrap gap-1.5">
                  {entry.disciplines.map((d) => (
                    <Badge key={d} variant="secondary" className="text-[10px]">{d.replace(/_/g, " ")}</Badge>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* Relations bilatérales */}
          <EntityRelationsWidget
            entityType="directory_entry"
            entityId={entry.id}
            entityName={entry.name}
            entityOwnerId={entry.owner_user_id}
            title="Athlètes / clubs partenaires"
            icon={Handshake}
          />
        </div>

        <div className="space-y-4">
          <Card className="p-4">
            <h3 className="font-display font-semibold text-sm mb-3">Coordonnées</h3>
            <div className="space-y-2 text-sm">
              {(entry.commune || entry.island) && (
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
                  <span>{[entry.address, entry.commune, entry.island].filter(Boolean).join(", ")}</span>
                </div>
              )}
              {entry.website && (
                <a href={entry.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[hsl(var(--aito-lagoon))] hover:underline">
                  <Globe className="h-4 w-4" /> Site internet
                </a>
              )}
              {entry.contact_email && (
                <a href={`mailto:${entry.contact_email}`} className="flex items-center gap-2 hover:underline">
                  <Mail className="h-4 w-4 text-muted-foreground" /> {entry.contact_email}
                </a>
              )}
              {entry.contact_phone && (
                <a href={`tel:${entry.contact_phone}`} className="flex items-center gap-2 hover:underline">
                  <Phone className="h-4 w-4 text-muted-foreground" /> {entry.contact_phone}
                </a>
              )}
              {entry.social_facebook && (
                <a href={entry.social_facebook} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline">
                  <Facebook className="h-4 w-4 text-muted-foreground" /> Facebook
                </a>
              )}
              {entry.social_instagram && (
                <a href={entry.social_instagram} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline">
                  <Instagram className="h-4 w-4 text-muted-foreground" /> Instagram
                </a>
              )}
              {(entry.external_profiles || []).map((p, i) => (
                <a key={i} href={p.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:underline">
                  <Globe className="h-4 w-4 text-muted-foreground" /> {p.label}
                </a>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
